import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Nav UI v2 — resolves the Sector list + the active sector's full link set
// for a given branch. The "active" choice is a lightweight per-branch
// localStorage preference (lets a user freely switch sectors at will,
// per-device) that defaults to whatever the backend resolves as their
// admin-assigned default (or that branch's auto-seeded "Default" sector).
export const useNavSectors = (branchId) => {
  const [sectors, setSectors] = useState([]);
  const [defaultSectorId, setDefaultSectorId] = useState(null);
  const [activeSectorId, setActiveSectorId] = useState(null);
  const [activeSector, setActiveSector] = useState(null);
  const [blueprintSector, setBlueprintSector] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSectors = useCallback(() => {
    if (!branchId) { setLoading(false); return; }
    setLoading(true);
    api.get(`${API}/nav-header/sectors`, { params: { branch_id: branchId } })
      .then(r => {
        const list = r.data?.sectors || [];
        const resolvedDefault = r.data?.default_sector_id || (list[0]?.id ?? null);
        setSectors(list);
        setDefaultSectorId(resolvedDefault);
        const stored = localStorage.getItem(`nav_sector_active_${branchId}`);
        const isValid = list.some(s => s.id === stored);
        setActiveSectorId(isValid ? stored : resolvedDefault);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { loadSectors(); }, [loadSectors]);

  useEffect(() => {
    if (!activeSectorId) { setActiveSector(null); return; }
    let cancelled = false;
    api.get(`${API}/nav-header/sectors/${activeSectorId}`)
      .then(r => { if (!cancelled) setActiveSector(r.data); })
      .catch(() => { if (!cancelled) setActiveSector(null); });
    return () => { cancelled = true; };
  }, [activeSectorId]);

  // The branch's is_default sector — the fixed "blueprint" whose rendered
  // width reserves the minimum space for the middle links slot, so the
  // Search/Sector/Delivery/Call anchors never move when switching sectors.
  useEffect(() => {
    const blueprintId = sectors.find(s => s.is_default)?.id;
    if (!blueprintId) { setBlueprintSector(null); return; }
    let cancelled = false;
    api.get(`${API}/nav-header/sectors/${blueprintId}`)
      .then(r => { if (!cancelled) setBlueprintSector(r.data); })
      .catch(() => { if (!cancelled) setBlueprintSector(null); });
    return () => { cancelled = true; };
  }, [sectors]);

  const switchSector = useCallback((sectorId) => {
    setActiveSectorId(sectorId);
    if (branchId) localStorage.setItem(`nav_sector_active_${branchId}`, sectorId);
    // Tell the home page, which follows the Space in use (spaces/useActiveSpace.js).
    window.dispatchEvent(new Event('c30:nav-sector-changed'));
  }, [branchId]);

  return { sectors, activeSectorId, activeSector, blueprintSector, switchSector, defaultSectorId, loading, reload: loadSectors };
};
